import { action, computed, observable, makeObservable } from 'mobx';

import {
    ContactPersonData,
    EhrData as EhrResponseParams,
    EhrBlockParams,
    EhrTypeOfWork,
    FileViewModel,
    InformedConsentData,
    InformedConsentStatus,
    Optional,
    Sex,
    EhrTranslationData,
    EhrChatData,
    SavedPaymentMethod,
} from 'chaika-frontend-types/frontend-cms';
import type { Nullable } from 'chaika-common-types';

import { FieldHistoryList } from './models/FieldHistoryList';
import { ContactPerson, ContactPersonParams } from './models/ContactPerson';
import { SubscriptionList } from '@store/models/SubscriptionList';
import { RepresentativeList } from './models/RepresentativeList';
import { ConfidantList } from './models/ConfidantList';
import { VaccinationList } from './models/VaccinationList';
import { MedicalCaseList } from '@store/models/SystemFields/MedicalCase';

import { ReservationList } from '@store/models/ReservationList';
import { ReservationDataWithAssociatedServices } from 'chaika-frontend-types/frontend-cms';
import { Person, PersonNameParams, PersonParams } from '@store/models/Person';
import { EntryList } from './models/EntryList';
import { EntryFields } from '@store/models/EntryFields';

import { removeNotNumbers } from '@utils/removeNotNumbers';
import { DateHelper, DateFormat, DateType } from '@utils/DateHelper';
import { formatAgeString } from '@utils/formatAgeString';
import { formatBirthDateWithAge } from '@utils/formatBirthDateWithAge';
import { formatCardNumber } from '@utils/formatCardNumber';
import { FileAsset } from '@store/models/FileAsset';
import { RenderedServiceList } from '@store/models/SystemFields/RenderedService';
import { EhrIdentityDocType, Locale, PrimaryChannel } from 'chaika-enums';
import { ReferralList } from '@store/models/SystemFields/Referral';
import { Validation } from '@utils/Validation';
import { PersonalAccountList } from '@patient/store/ehr/models/PersonalAccountList';
import { PatientReservationList } from '@patient/store/ehr/PatientReservationList';
import { isChatWithDoctor } from '@patient/utils/isChatWithDoctor';
import { translate } from '@chaika/i18n/helper';
import { isDefined } from '@utils/isDefined';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import compact from 'lodash/compact';
import { AssistantPracticeChat } from './types';
import { RecommendationList } from '@store/models/SystemFields/MedicalCase/RecommendationList';

interface SerializedEhrParams extends Omit<EhrResponseParams, 'mergedEhrIds' | 'mainEhrId' | 'birthDate'> {
    birthDate?: string;
}

export class Ehr extends Person {
    public static Name = 'ehr' as const;

    @observable public id!: string;
    @observable public recordId!: number;
    @observable public creationTime!: string;
    @observable public mergedEhrIds!: Set<string>;
    @observable public mainEhrId?: string;
    @observable public sex!: Sex;
    @observable public identityDocType?: EhrIdentityDocType;
    @observable public email: Optional<string>;
    @observable public isDocumentsSigned: Optional<boolean>;
    @observable public legalDocConfirm = false;
    @observable public extraDocConfirm = false;
    @observable public isPersonalAreaCreated: Optional<boolean>;
    @observable public inn: Optional<string>;
    @observable public phone: Optional<string>;
    @observable public placeOfWork: Optional<string>;
    @observable public typeOfWork: Optional<EhrTypeOfWork>;
    @observable public comment: Optional<string>;
    @observable public paymentComment: Optional<string>;
    @observable public identityDoc: Optional<string>;
    @observable public identityDocDate: Optional<string>;
    @observable public identityDocIssuer: Optional<string>;
    @observable public identityDocIssuerTransliteration: Optional<string>;
    @observable public identityDocCode: Optional<string>;
    @observable public internationalIdNumber: Optional<string>;
    @observable public internationalIdIssuingStateCode: Optional<string>;
    @observable public internationalIdIssueDate: Optional<string>;
    @observable public internationalIdExpiryDate: Optional<string>;
    @observable public internationalIdAuthority: Optional<string>;
    @observable public internationalIdAuthorityTransliteration: Optional<string>;
    @observable public registrationAddress: Optional<string>;
    @observable public registrationAddressTransliteration: Optional<string>;
    @observable public address: Optional<string>;
    @observable public addressTransliteration: Optional<string>;
    @observable public contactPersons: ContactPerson[];
    @observable public fields = new EntryFields();
    @observable public fieldHistory = new FieldHistoryList();
    @observable public entryList = new EntryList();
    @observable public subscriptions = new SubscriptionList();
    @observable public cards?: SavedPaymentMethod[];
    @observable public availableReservationList = new ReservationList();
    @observable public reservationList = new ReservationList<ReservationDataWithAssociatedServices>();
    @observable public activeReservations = new ReservationList<ReservationDataWithAssociatedServices>();
    @observable public patientReservations: PatientReservationList = new PatientReservationList();
    @observable public activePatientReservations: ReservationDataWithAssociatedServices[] = [];
    @observable public representativeList = new RepresentativeList();
    @observable public confidantList = new ConfidantList();
    @observable public medicalCases = new MedicalCaseList();
    @observable public recommendations = new RecommendationList();
    @observable public translations: Map<Locale, EhrTranslationData>;

    @observable public vaccinationList = new VaccinationList();
    @observable public vaccinationFileNames = new Map<string, string>();

    // если с декоратором, то возникает баг при добавлении файлов.
    // Добавили файл Файл1. Добавляем файл Файл2. Результат имеем два Файл2 =(
    public files: FileAsset[] = [];
    @observable public personalAccountBalance: Optional<number>;
    @observable public personalAccountItems = new PersonalAccountList();
    @observable public showBlockBanner: boolean = false;
    @observable public blockBannerUrl?: string;
    @observable public isBlocked: boolean = false;
    @observable public todayRenderedServices: RenderedServiceList = new RenderedServiceList([]);
    @observable public subscriptionRenderedServices: RenderedServiceList = new RenderedServiceList([]);
    @observable public referralList: ReferralList = new ReferralList();
    @observable public vaccinationReferralList: ReferralList = new ReferralList();
    @observable public entryCount?: number;
    @observable public unpaidRenderedServiceCount?: number;
    @observable public tgChatLinks: Optional<EhrChatData[]>;

    @observable private assistantsChats_?: AssistantPracticeChat[];
    @observable private blockedBy_?: string;
    @observable private blockComment_?: string;
    @observable private snils_: Optional<string>;
    @observable private birthDate_: Optional<Date>;
    @observable private informedConsents = new Map<string, InformedConsentData | null>();

    public constructor(
        {
            id,
            recordId,
            mergedEhrIds = [],
            mainEhrId,
            birthDate,
            email,
            sex,
            snils,
            inn,
            phone,
            placeOfWork,
            typeOfWork,
            comment,
            paymentComment,
            contactPersons = [],
            identityDoc,
            identityDocDate,
            identityDocType,
            identityDocIssuer,
            identityDocIssuerTransliteration,
            identityDocCode,
            internationalIdNumber,
            internationalIdIssuingStateCode,
            internationalIdIssueDate,
            internationalIdExpiryDate,
            internationalIdAuthority,
            internationalIdAuthorityTransliteration,
            registrationAddress,
            registrationAddressTransliteration,
            address,
            addressTransliteration,
            isDocumentsSigned,
            legalDocConfirm = false,
            extraDocConfirm = false,
            isPersonalAreaCreated,
            isBlocked = false,
            blockedBy,
            blockComment,
            creationTime,
            translations,
            ...person
        }: EhrResponseParams = {} as EhrResponseParams,
    ) {
        super(person as PersonParams);
        makeObservable(this);

        this.id = id;
        this.recordId = recordId;
        this.mergedEhrIds = new Set(mergedEhrIds);
        this.mainEhrId = mainEhrId;
        this.birthDate_ = birthDate ? new Date(birthDate) : undefined;
        this.email = email;
        this.isDocumentsSigned = isDocumentsSigned;
        this.isPersonalAreaCreated = isPersonalAreaCreated;
        this.legalDocConfirm = legalDocConfirm;
        this.extraDocConfirm = extraDocConfirm;
        this.identityDocType = identityDocType;
        this.sex = sex;
        this.snils_ = snils;
        this.inn = inn;
        this.phone = phone;
        this.comment = comment;
        this.paymentComment = paymentComment;
        this.placeOfWork = placeOfWork;
        this.typeOfWork = typeOfWork;
        this.identityDoc = identityDoc;
        this.identityDocDate = identityDocDate;
        this.identityDocIssuer = identityDocIssuer;
        this.identityDocIssuerTransliteration = identityDocIssuerTransliteration;
        this.identityDocCode = identityDocCode;
        this.internationalIdNumber = internationalIdNumber;
        this.internationalIdIssuingStateCode = internationalIdIssuingStateCode;
        this.internationalIdIssueDate = internationalIdIssueDate;
        this.internationalIdExpiryDate = internationalIdExpiryDate;
        this.internationalIdAuthority = internationalIdAuthority;
        this.internationalIdAuthorityTransliteration = internationalIdAuthorityTransliteration;
        this.registrationAddress = registrationAddress;
        this.registrationAddressTransliteration = registrationAddressTransliteration;
        this.address = address;
        this.addressTransliteration = addressTransliteration;
        this.contactPersons = contactPersons.map(person => new ContactPerson(person));
        this.isBlocked = isBlocked;
        this.blockedBy_ = blockedBy;
        this.blockComment_ = blockComment;
        this.creationTime = creationTime;
        this.translations = new Map(translations?.map(it => [it.locale, it]));
    }

    @computed
    public get isSubscriptionsLoaded(): boolean {
        return this.subscriptions.isDataSet;
    }

    @computed
    public get isCardLoaded(): boolean {
        return !!this.cards;
    }
    @computed
    public get assistantsChats(): AssistantPracticeChat[] {
        return this.assistantsChats_ ?? [];
    }
    @computed
    public get isAssistantsChatsSet(): boolean {
        return isDefined(this.assistantsChats_);
    }

    public get snils(): Optional<string> {
        return this.snils_;
    }

    @computed
    public get birthDate(): Optional<string> {
        return this.birthDate_ && DateHelper.format(this.birthDate_, DateFormat.DateWithDotSeparator);
    }

    @computed
    public get rawBirthDate(): Optional<Date> {
        return this.birthDate_ ?? undefined;
    }

    @computed
    public get birthDateWithAge(): string {
        return formatBirthDateWithAge(this.birthDate_);
    }

    @computed
    public get engBirthDateWithAge(): string {
        return `${this.birthDate} ${this.ageStringEng}`;
    }

    @computed
    public get cardNumber(): string {
        return formatCardNumber(this.recordId);
    }

    @computed
    public get sexString(): string {
        return this.sex === Sex.Female
            ? translate('woman')
            : this.sex === Sex.Male
              ? translate('man')
              : translate('notSpecified');
    }

    @computed
    public get engSexString(): string {
        return this.sex === Sex.Female ? 'Female' : this.sex === Sex.Male ? 'Male' : 'Unknown';
    }

    @computed
    public get isSexSet(): boolean {
        return !!this.sex;
    }

    @computed
    public get age(): Optional<number> {
        return this.birthDate_ && (DateHelper.differenceInYears(new Date(), this.birthDate_) || 0);
    }

    @computed
    public get ageString(): string {
        return this.birthDate_ ? formatAgeString(this.birthDate_) : '';
    }

    @computed
    public get ageStringEng(): string {
        return this.birthDate_ ? formatAgeString(this.birthDate_, Locale.En) : '';
    }

    @computed
    public get hasConfidants(): boolean {
        return this.confidantList.getSize() > 0;
    }

    @computed
    public get hasBankCard(): boolean {
        return !!this.cards && this.cards.length > 0;
    }

    @computed
    public get blockedBy(): Optional<string> {
        return this.blockedBy_;
    }

    @computed
    public get blockComment(): Optional<string> {
        return this.blockComment_;
    }

    @computed
    public get fullInfo(): string {
        return `${this.shortFullName}, ${this.cardNumber}, ${this.birthDate}, ${this.phone}`;
    }

    @computed
    public get primaryPatient(): boolean {
        return this.entryCount === 0;
    }

    @computed
    public get hasDept(): boolean {
        return (this.unpaidRenderedServiceCount || 0) > 0;
    }

    @computed
    public get isUnpaidRenderedServiceLoaded(): boolean {
        return isDefined(this.unpaidRenderedServiceCount);
    }

    public get hasContacts(): boolean {
        return !!((this.phone && Validation.isPhone(this.phone)) || (this.email && Validation.isEmail(this.email)));
    }

    public set snils(snils: Optional<string>) {
        this.snils_ = snils && removeNotNumbers(snils);
    }

    public set birthDate(newBirthDate: Optional<string>) {
        if (newBirthDate && DateHelper.isValid(new Date(newBirthDate))) {
            this.birthDate_ = new Date(newBirthDate);
        }
    }

    public getFullNameByLang(locale: Locale): string {
        const translation = this.translations.get(locale);
        let fullName = '';

        if (translation?.firstName && translation?.lastName) {
            fullName = this.createFullNameString(translation as PersonNameParams);
        } else {
            fullName = this.createFullNameString({
                firstName: this.firstName,
                lastName: this.lastName,
                oldLastName: this.oldLastName,
                patronymic: this.patronymic,
            });
        }

        return fullName;
    }

    public getInitialsByTranslation(locale: Locale): string {
        const translation = this.translations.get(locale);
        let initials = '';

        if (translation?.firstName && translation?.lastName) {
            initials = this.createInitialsString(translation as PersonNameParams);
        } else {
            initials = this.createInitialsString({
                firstName: this.firstName,
                lastName: this.lastName,
                patronymic: this.patronymic,
            });
        }

        return initials;
    }

    public getShortFullNameByTranslation(locale: Locale): string {
        const translation = this.translations.get(locale);

        let shortFullName = '';

        if (translation?.firstName && translation?.lastName) {
            shortFullName = `${translation.lastName || ''} ${translation.firstName || ''}`;
        } else {
            shortFullName = `${this.lastName || ''} ${this.firstName || ''}`;
        }

        return shortFullName;
    }

    public hasTodayRenderedService(serviceId: string): boolean {
        return !!this.todayRenderedServices.values.find(service => service.serviceId === serviceId);
    }

    public getDateBirthDate(): Optional<Date> {
        return this.birthDate_;
    }

    public isMinor(): boolean {
        return isDefined(this.age) && this.age < 18;
    }

    public isMinorOnDate(date: DateType): boolean {
        const ageOnDate = isDefined(this.birthDate_) ? DateHelper.differenceInYears(date, this.birthDate_) : undefined;

        return isDefined(ageOnDate) && ageOnDate < 18;
    }

    public hasRepresentative(): boolean {
        return this.representativeList.values.length > 0;
    }

    public getTgChatData(doctorId: string): Optional<EhrChatData> {
        return this.tgChatLinks?.find(link => isChatWithDoctor(link, doctorId));
    }

    public isInformedConsentSigned(reservationId: string, serviceId: string): boolean {
        const id = this.makeInformedConsentId(reservationId, serviceId);
        return this.informedConsents.get(id)?.status === InformedConsentStatus.Signed;
    }

    public hasInformedConsent(reservationId: string, serviceId: string): boolean {
        const id = this.makeInformedConsentId(reservationId, serviceId);
        return this.informedConsents.has(id) && this.informedConsents.get(id) !== null;
    }

    @action
    public setInformedConsent(reservationId: string, serviceId: string, params: InformedConsentData | null): void {
        const id = this.makeInformedConsentId(reservationId, serviceId);
        this.informedConsents.set(id, params);
    }

    @action
    public removeContactPerson(removedContactPerson: ContactPerson) {
        this.contactPersons = this.contactPersons.filter(
            contactPerson =>
                contactPerson.fullName !== removedContactPerson.fullName &&
                contactPerson.phone !== removedContactPerson.phone,
        );
    }

    @action
    public setContactPersons(params: ContactPersonParams[]): void {
        this.contactPersons = params.map(contactPerson => new ContactPerson(contactPerson));
    }

    @action
    public addContactPerson(params: ContactPersonParams): void {
        this.contactPersons.push(new ContactPerson(params));
    }

    @action
    public setPrimaryChannel(primaryChannel: Nullable<PrimaryChannel>): void {
        this.primaryChannel = primaryChannel;
    }

    @action
    public setPaymentComment(paymentComment: string): void {
        this.paymentComment = paymentComment;
    }

    @action
    public setTgChatLinks(tgChatLinks: EhrChatData[]): void {
        this.tgChatLinks = tgChatLinks;
    }

    public hasTgChatLinks(tgChatLinks: EhrChatData[]): void {
        this.tgChatLinks = tgChatLinks;
    }

    @action
    public setAssistantsChats(chats: AssistantPracticeChat[]): void {
        this.assistantsChats_ = chats;
    }

    public serialize(): SerializedEhrParams {
        return {
            ...super.serialize(),
            id: this.id,
            recordId: this.recordId,
            birthDate: this.birthDate,
            email: this.email,
            sex: this.sex,
            snils: this.snils,
            inn: this.inn,
            phone: this.phone,
            comment: this.comment,
            isPersonalAreaCreated: !!this.isPersonalAreaCreated,
            isDocumentsSigned: !!this.isDocumentsSigned,
            creationTime: this.creationTime,
            legalDocConfirm: this.legalDocConfirm,
            extraDocConfirm: this.extraDocConfirm,
            paymentComment: this.paymentComment,
            placeOfWork: this.placeOfWork,
            typeOfWork: this.typeOfWork,
            contactPersons: this.serializeContactPersons(),
            identityDoc: this.identityDoc,
            identityDocDate: this.identityDocDate,
            identityDocIssuer: this.identityDocIssuer,
            identityDocIssuerTransliteration: this.identityDocIssuerTransliteration,
            identityDocCode: this.identityDocCode,
            internationalIdNumber: this.internationalIdNumber,
            internationalIdIssuingStateCode: this.internationalIdIssuingStateCode,
            internationalIdIssueDate: this.internationalIdIssueDate,
            internationalIdExpiryDate: this.internationalIdExpiryDate,
            internationalIdAuthority: this.internationalIdAuthority,
            internationalIdAuthorityTransliteration: this.internationalIdAuthorityTransliteration,
            registrationAddress: this.registrationAddress,
            registrationAddressTransliteration: this.registrationAddressTransliteration,
            address: this.address,
            addressTransliteration: this.addressTransliteration,
            translations: this.translations ? toArrayFromIterable(this.translations) : undefined,
            blockComment: this.blockComment,
        };
    }

    public serializeContactPersons(): ContactPersonData[] {
        return this.contactPersons.map(person => person.serialize());
    }
    //
    // public getMedicalCaseDetails(
    //     medicalCaseId: string,
    //     fieldValues?: FiledValueParams<DiagnosisList>[],
    // ): DetailsDetailsParams[] {
    //     const values =
    //         fieldValues ||
    //         this.fieldHistory.getFieldValuesByNameAndId<DiagnosisList>(EntryTypeFieldName.MedicalCases, medicalCaseId);
    //
    //     return values.map((fieldValue, index) => {
    //         const entry = this.entryList.get(fieldValue.entryId)!;
    //
    //         return {
    //             entryId: fieldValue.entryId,
    //             creationTime: fieldValue.creationTime,
    //             author: fieldValue.author,
    //             title: entry.entryTypeTitle,
    //             statuses: this.calculateStatuses(medicalCaseId, values, index),
    //         };
    //     });
    // }

    @action
    public addFile(file: FileViewModel) {
        const fileAsset = new FileAsset(file);
        this.files.push(fileAsset);
    }

    @action
    public setFiles(files: FileViewModel[]) {
        this.files = files.map(file => new FileAsset(file));
    }

    @action
    public deleteFile(name: string) {
        this.files = this.files.filter(file => file.name !== name);
    }

    @action
    public block({ userId, comment }: EhrBlockParams): void {
        this.isBlocked = true;
        this.blockedBy_ = userId;
        this.blockComment_ = comment;
        this.showBlockBanner = true;
    }

    @action
    public unblock(): void {
        this.isBlocked = false;
        this.blockedBy_ = undefined;
        this.blockComment_ = undefined;
        this.showBlockBanner = false;
    }

    private createInitialsString({ firstName, lastName, patronymic }: PersonNameParams): string {
        return `${lastName || ''}${firstName ? this.getFirstLetter(firstName) : ''}${
            patronymic ? this.getFirstLetter(patronymic) : ''
        }`;
    }

    private createFullNameString({ firstName, lastName, oldLastName, patronymic }: PersonNameParams) {
        return compact([lastName, oldLastName, firstName, patronymic]).join(' ');
    }

    private makeInformedConsentId(reservationId: string, serviceId: string): string {
        return `${reservationId}_${serviceId}`;
    }
}

export const ehr = new Ehr();
